import type { Media } from 'types';
import type { WrapProps } from '@leafwell/components';
import type { PageProps } from 'lib/queries/pages';
import type { InPersonEvents } from 'types/in-person-events';
import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Image from 'next/image';
import camelcaseKeys from 'camelcase-keys';
import { Button, Wrap } from '@leafwell/components';
import SignupForm from 'components/signup-form';
import HeadingBody from 'components/heading-body';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { request } from 'lib/rest';
import { useCurrentState } from 'hooks/useCurrentState';
import {
  getEventDay,
  getEventsMonth,
  hasEvent,
} from 'lib/utils/eventsDateUtils';
import { calculateVpdDiscount } from 'lib/utils/calculateVpdDiscount';
import { usePostHog } from 'posthog-js/react';
import WysiwygContent from './wysiwyg-content';

export type HeroCardProps = Partial<Pick<WrapProps, 'className' | 'spacer'>> &
  Pick<
    PageProps['page']['acf'],
    | 'hero'
    | 'states'
    | 'formUrl'
    | 'pageType'
    | 'affiliateLogo'
    | 'affiliateWebsite'
    | 'pauseAppointments'
    | 'redirectMedicalCard'
    | 'redirectProductEndpoint'
    | 'redirectCannabisCounseling'
    | 'totalMedicalCardPrice'
    | 'city'
    | 'isClinic'
  > & {
    redirectStatePage?: boolean;
    inPersonEvents?: InPersonEvents;
    showTabs?: boolean;
    passwordless?: boolean;
  };

type PartnerProps = {
  title: string;
  logo: Media;
  website: string;
  couponText?: string;
  event?: {
    uri: string;
    discount: string;
    startDate: string;
    endDate: string;
  };
};

const AffiliateLogo = dynamic(() => import('./affiliate-logo'), {
  loading: () => <div className="h-16"></div>,
  ssr: false,
});

const VpdEventAlert: React.FC<PartnerProps['event']> = ({
  startDate,
  endDate,
  discount,
  uri,
}) => {
  const startMonth = getEventsMonth(startDate);
  const endMonth = getEventsMonth(endDate);
  const startDay = getEventDay(startDate);
  const endDay = getEventDay(endDate);
  const dateText =
    startMonth !== endMonth
      ? `${startDay} ${startMonth} and ${endDay} ${endMonth}`
      : `${startDay} and ${endDay} of ${startMonth}`;

  return (
    <>
      <p className="text-center lg:text-left text-white lg:text-black">
        <strong>Save ${discount}</strong> - {dateText} only
      </p>
      <Button
        className="text-xs lg:text-sm min-h-fit !py-2 !px-2"
        rel="noopener"
        label="Sign up here"
        href={uri}
        rounded={true}
        variant="secondary"
        filled={true}
      />
    </>
  );
};

const HeroCardForm: React.FC<
  HeroCardProps & {
    redirectOnStateChange?: boolean;
  }
> = ({
  hero,
  states,
  formUrl,
  pageType = 'card',
  redirectOnStateChange = true,
  redirectMedicalCard,
  redirectProductEndpoint,
  redirectCannabisCounseling,
  affiliateLogo,
  totalMedicalCardPrice,
  // affiliateWebsite,
  inPersonEvents,
  showTabs = true,
  city,
  isClinic,
  passwordless = false,
}) => {
  const posthog = usePostHog();
  const router = useRouter();
  const { query, asPath } = router;
  const [partnerData, setPartnerData] = useState<PartnerProps | null>(null);
  const [activeTab, setActiveTab] = useState<'new' | 'renew'>('new');
  const [isReferred, setIsReferred] = useState<boolean>(false);
  const [referrerName, setReferrerName] = useState<string | null>(null);
  const currentState = useCurrentState(states);

  const quickLinks = hero.quickLinks;

  useEffect(() => {
    if (query.utm_source && !partnerData) {
      // call api to fetch partner data based on `utm_source`
      request(`/partner?slug=${query.utm_source}`).then(res => {
        if (res.data && res.data?.totalItems > 0) {
          const { title, acf } = camelcaseKeys(res.data.items[0], {
            deep: true,
          });
          const { logo, website, couponText, states, vpdPage } = acf;
          let event = null;

          if (vpdPage?.events?.length && vpdPage?.price) {
            const currentEvent = vpdPage.events.filter(
              ({ eventStartDate, eventEndDate }) =>
                hasEvent(eventStartDate, eventEndDate),
            );

            if (currentEvent?.length) {
              const discount = calculateVpdDiscount(
                vpdPage.price?.price,
                vpdPage.price?.newPrice,
              );

              event = {
                uri: vpdPage.uri,
                discount,
                startDate: currentEvent[0]?.eventStartDate,
                endDate: currentEvent[0]?.eventEndDate,
              };
            }
          }

          if (
            !states.length ||
            (states.length && states.includes(currentState))
          ) {
            setPartnerData({
              title,
              logo,
              website,
              couponText,
              event,
            });
          }
        }
      });
    }
  }, [query]);

  useEffect(() => {
    if (query.ref_id) {
      setIsReferred(true);
      posthog.identify(posthog.get_distinct_id(), {
        ref_id: query.ref_id as string,
        referral_type: query?.discount_code ? 'email' : 'sms',
      });
    }
    if (query.name) {
      setReferrerName(query.name as string);
    }
  }, [query.ref_id, query.name]);

  useEffect(() => {
    if (query.ref_id && !query.discount_code) {
      fetch('/api/get-discount-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: 15 }),
      })
        .then(res => res.json())
        .then(({ code }) => {
          if (!code) return;
          router.replace(
            {
              pathname: router.pathname,
              query: { ...router.query, discount_code: code },
            },
            undefined,
            { shallow: true },
          );
        })
        .catch(err => console.error('[WOM] get-discount-code failed:', err));
    }
  }, [query.ref_id, query.discount_code]);

  useEffect(() => {
    if (query.knowledgebase_id) {
      const wuithoutSearchParams = asPath.split('?')[0];
      const newUrl = `${wuithoutSearchParams}#${query.knowledgebase_id}`;
      router.push(newUrl);
    }
  }, [query]);

  return (
    <section className="relative grid bg-lilac-50 xl:-mt-10 xl:pt-10 lw-state-pages">
      {isReferred ? (
        <div className="mx-4 lg:mx-auto py-2 md:py-4 max-w-screen-2xl bg-green-50 border border-green-200 rounded-xl mt-4 md:mt-0">
          <div className="flex items-center justify-start px-4 gap-x-6">
            <p className="font-semibold uppercase text-dark-green rounded-full px-2 py-1 bg-lilac-50 border border-green-200 text-xs shadow-sm">
              Referred
            </p>
            <div>
              <p className="font-semibold leading-tight text-base lg:text-lg">
                {referrerName
                  ? `${referrerName} shared this with you! Sign up to get your discount.`
                  : 'A friend shared this with you! Sign up to get your discount.'}
              </p>
            </div>
          </div>
        </div>
      ) : null}
      <Wrap className="lg:grid-cols-auto-auto lg:gap-x-16 items-start">
        <div className="relative flex flex-col gap-y-6 lg:gap-y-8 text-pretty">
          <div className="flex items-center gap-x-1.5 font-semibold">
            <Image src="/trustpilot-icon.svg" alt="" width={19} height={17} />
            Trusted By <span className="text-purple-500">1 Million</span>{' '}
            Monthly Users
          </div>
          <HeadingBody
            level="1"
            title={
              activeTab === 'renew' && hero?.renewTitle?.length > 0
                ? hero.renewTitle
                : hero.title
            }
            className="font-semibold"
          />
          {hero.description?.length > 0 ? (
            <WysiwygContent
              className="!text-base xl:!text-2xl !text-gray-700"
              content={
                activeTab === 'renew' && hero?.renewDescription?.length > 0
                  ? hero.renewDescription
                  : hero.description
              }
            />
          ) : null}
          {partnerData ? (
            <div>
              <div
                className={`lg:inline-flex flex flex-col items-center gap-y-6 mt-6 lg:mt-0 ${
                  partnerData ? 'opacity-1 ' : 'opacity-0'
                } transition-opacity duration-500 lg:px-5 lg:py-5 lg:rounded-2xl lg:border lg:border-x lg:radius-20px bg-white border-lilac-100`}
              >
                <div className="flex flex-col md:flex-row justify-center gap-6">
                  {partnerData?.logo ? (
                    <div className="self-center justify-self-center">
                      <AffiliateLogo
                        affiliateLogo={affiliateLogo || partnerData.logo}
                      />
                    </div>
                  ) : null}
                  <div className="flex flex-col justify-center text-center md:text-left gap-y-4">
                    {!partnerData?.couponText ? (
                      <p className="text text-pretty text-lg">
                        Leafwell is proud to partner <br />
                        with <strong>{partnerData.title}</strong>
                      </p>
                    ) : null}
                    {partnerData?.couponText ? (
                      <div
                        className="inline-block"
                        dangerouslySetInnerHTML={{
                          __html: partnerData.couponText,
                        }}
                      />
                    ) : null}
                  </div>
                </div>
                {partnerData?.event && (
                  <div className="hidden lg:flex flex-col items-center gap-y-6 w-full">
                    <hr className="w-full border-lilac-100" />
                    <div className="flex flex-col gap-y-4">
                      <VpdEventAlert {...partnerData.event} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
        <div className="w-full justify-self-center">
          <SignupForm
            formUrl={formUrl}
            pageType={pageType}
            activeStates={states}
            redirectMedicalCard={redirectMedicalCard}
            redirectProductEndpoint={redirectProductEndpoint}
            redirectOnStateChange={redirectOnStateChange}
            redirectCannabisCounseling={redirectCannabisCounseling}
            formTitle={hero.formTitle}
            formSubmitText={hero.formSubmitText}
            totalMedicalCardPrice={totalMedicalCardPrice}
            inPersonEvents={inPersonEvents}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            renewFormContentItems={hero.renewFormContentItems}
            showTabs={showTabs}
            showOptInCheckbox={
              states?.length > 0 && states[0] === 'IL' && partnerData !== null
            }
            city={city || undefined}
            isClinic={isClinic}
            passwordless={passwordless}
          />
        </div>
        {quickLinks?.length > 0 && (
          <ul className="col-span-full flex flex-col md:flex-row gap-3 md:gap-6 divide-y md:divide-y-0 md:divide-x divide-[#C3CDD5]">
            {quickLinks.map((link, index) => {
              const isFirst = index === 0;
              return (
                <li
                  key={link.title}
                  className={`flex-1 pb-2 ${
                    isFirst ? 'md:pl-0' : 'md:pl-8 pt-8 md:pt-0'
                  }`}
                >
                  <a
                    href={`#${link.section}`}
                    className="flex flex-col gap-y-2 max-w-xl"
                  >
                    <div className="flex items-center gap-x-4">
                      {link.icon ? (
                        <Image
                          src={link.icon.src}
                          alt={link.icon.alt || ''}
                          width={48}
                          height={48}
                          className="w-12 h-12"
                        />
                      ) : null}
                      <p className="text-xl font-semibold">{link.title}</p>
                      <Image
                        src="/chevron-down.svg"
                        alt=""
                        width={24}
                        height={24}
                      />
                    </div>
                  </a>
                </li>
              );
            })}
          </ul>
        )}
      </Wrap>
    </section>
  );
};

const HeroCard: React.FC<HeroCardProps> = props => {
  const { disableMedicalCard } = useWordPressContext();
  const {
    redirectStatePage,
    affiliateLogo,
    pauseAppointments,
    redirectMedicalCard,
  } = props;

  let redirectOnStateChange = true;

  if (redirectStatePage) {
    redirectOnStateChange = redirectStatePage;
  }

  if (disableMedicalCard) {
    redirectOnStateChange = false;
  }

  // this is an affiliate page
  if (affiliateLogo) {
    redirectOnStateChange = false;
  }

  // this page isn't active on medical card and a hubspot form is displayed
  if (pauseAppointments || !redirectMedicalCard) {
    redirectOnStateChange = false;
  }

  return (
    <HeroCardForm
      redirectMedicalCard={redirectMedicalCard}
      redirectOnStateChange={redirectOnStateChange}
      {...props}
    />
  );
};

export default HeroCard;
